import { useCallback } from 'react';
import { PageRequest } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { LimitedSchemaInfiniteQueryOptions, LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaDigitalForms } from '../../service';
import { IO } from '../../types';
import { defaultOptions } from '../config';
import { requestKeys } from '../keys';

/**
 * @param companyId - AKA locationId
 */
export const useFormList = <E = unknown, T = IO.GetFormList['output']>(
  req: Compulsory<IO.GetFormList['input'], 'companyId'>,
  options?: LimitedSchemaInfiniteQueryOptions<IO.GetFormList, E, T>
) => {
  const queryKey = requestKeys.formList(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      SchemaDigitalForms.GetFormList(
        { ...req, page: pageParam || req.page },
        { headers: { 'Location-Id': req.companyId } }
      ),
    getNextPageParam: (lastPage: IO.GetFormList['output']) => lastPage.pageMetadata?.page,
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

/**
 * @param companyIds - AKA locationIds
 * @deprecated Use `useFormListV3` instead
 */
export const useFormListV2 = <E = unknown, T = IO.GetFormListV2['output']>(
  req: Compulsory<IO.GetFormListV2['input'], 'companyIds' | 'pageRequest'>,
  options?: LimitedSchemaInfiniteQueryOptions<IO.GetFormListV2, E, T>
) => {
  const queryKey = requestKeys.formListV2(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: PageRequest }) =>
      SchemaDigitalForms.GetForms({
        ...req,
        pageRequest: pageParam || req.pageRequest,
      }),
    getNextPageParam: (lastPage): PageRequest | undefined => {
      const page = lastPage.pageMetadata?.page;
      return {
        pageNumber: page ? parseInt(page, 10) : undefined,
        pageLimit: req.pageRequest.pageLimit,
      };
    },
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

type GetFormsListV3Request = Compulsory<IO.GetFormListV3['input'], 'orgRequest' | 'pageRequest'>;
type ModifiedFormsListV3Response = Omit<IO.GetFormListV3['output'], 'data'> & {
  rows: IO.GetFormListV3['output']['data'];
};

/**
 * Fetches the list of forms in the organization and returns the response
 * in a format that is compatible with the InfinitePaginatedList component.
 */
async function getFormsListV3(req: GetFormsListV3Request): Promise<ModifiedFormsListV3Response> {
  const response = await SchemaDigitalForms.GetFormsV3(req);
  return {
    pageMetadata: response.pageMetadata,
    rows: response.data ?? [], // This is required for the InfinitePaginatedList component to work.
  };
}

interface GetFormListV3ModifiedIO {
  input: GetFormsListV3Request;
  output: ModifiedFormsListV3Response;
}
export interface FormListV3Response {
  rows: IO.GetFormListV3['output']['data'];
}

/**
 * Fetches the list of forms in the organization.
 */
export const useFormListV3 = <E = unknown, T = GetFormListV3ModifiedIO['output']>(
  req: GetFormsListV3Request,
  options?: LimitedSchemaInfiniteQueryOptions<GetFormListV3ModifiedIO, E, T>
) => {
  const queryKey = requestKeys.formListV3(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: PageRequest }) =>
      getFormsListV3({ ...req, pageRequest: pageParam || req.pageRequest }),
    getNextPageParam: (lastPage): PageRequest | undefined => {
      const page = parseInt(lastPage.pageMetadata?.page || '0', 10);
      const lastPageNumber = parseInt(lastPage.pageMetadata?.lastPage || '0', 10);

      if (page >= lastPageNumber) {
        return undefined;
      }

      return {
        pageNumber: page + 1,
        pageLimit: req.pageRequest.pageLimit,
      };
    },
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

/**
 * Fetches the details of a form.
 */
export const useGetFormDetails = <E = unknown, T = IO.GetFormDetails['output']>(
  req: Compulsory<IO.GetFormDetails['input'], 'formId' | 'orgId'>,
  options?: LimitedSchemaQueryOptions<IO.GetFormDetails, E, T>
) => {
  const queryKey = requestKeys.formDetails(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetFormDetails(req),
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export const useGetFormDetailsAsync = <E = unknown, T = IO.GetFormDetails['output']>() => {
  const queryClient = useQueryClient();
  const fetchFormDetailsAsync = useCallback(
    async (
      req: Compulsory<IO.GetFormDetails['input'], 'formId' | 'orgId'>,
      options?: LimitedSchemaQueryOptions<IO.GetFormDetails, E, T>
    ) => {
      try {
        const response = await queryClient.fetchQuery(
          requestKeys.formDetails(req),
          () => SchemaDigitalForms.GetFormDetails(req),
          {
            ...defaultOptions,
            retry: false,
            ...options,
          }
        );
        return response;
      } catch (error) {
        console.error('Error fetching form details', error);
        return undefined;
      }
    },
    [queryClient]
  );

  return {
    fetchFormDetailsAsync,
  };
};

export const useGetESMedicalHistoryForms = <E = unknown, T = IO.GetESMedicalHistoryForms['output']>(
  req: Compulsory<IO.GetESMedicalHistoryForms['input'], 'companyId' | 'sourceId'>,
  options?: LimitedSchemaQueryOptions<IO.GetESMedicalHistoryForms, E, T>
) => {
  const queryKey = requestKeys.eaglesoftMedicalHistoryForms(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetESMedicalHistoryForms(req),
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export const useGetNonEditableForms = <E = unknown, T = IO.GetNonEditableForms['output']>(
  req: Compulsory<IO.GetNonEditableForms['input'], 'companyId'>,
  options?: LimitedSchemaQueryOptions<IO.GetNonEditableForms, E, T>
) => {
  const queryKey = requestKeys.nonEditableForms(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetFormTemplates(req),
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export const useGetFormPDF = <E = unknown, T = IO.GetFormPDFAuthed['output']>(
  req: Compulsory<IO.GetFormPDFAuthed['input'], 'formId' | 'companyId'>,
  options?: LimitedSchemaQueryOptions<IO.GetFormPDFAuthed, E, T>
) => {
  const queryKey = requestKeys.formPDF(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetFormPDFAuthed(req),
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export const useGetFormPDFAsync = <E = unknown, T = IO.GetFormPDFAuthed['output']>() => {
  const queryClient = useQueryClient();
  const fetchFormPDFAsync = useCallback(
    async (
      req: Compulsory<IO.GetFormPDFAuthed['input'], 'formId' | 'companyId'>,
      options?: LimitedSchemaQueryOptions<IO.GetFormPDFAuthed, E, T>
    ) => {
      const response = await queryClient.fetchQuery(
        requestKeys.formPDF(req),
        () => SchemaDigitalForms.GetFormPDFAuthed(req),
        {
          ...defaultOptions,
          ...options,
        }
      );
      return response;
    },
    [queryClient]
  );

  return {
    fetchFormPDFAsync,
  };
};
